import React, { useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../context/AuthContext';
import { SwallowMark } from '../common/SwallowMark';
import { TinycmsWordmark } from '../common/TinycmsWordmark';
import { uploadAsset } from '../../lib/media/uploadAsset';
import { isVideoFile, uploadVideo } from '../../lib/media/uploadVideo';
import { Loader } from '../common/PageLoader';

/**
 * The page behind an agent's request_media_upload link: the person uploads
 * photos or videos from their phone or computer, and the agent reads them back
 * by request id. Videos go straight to video hosting, in resumable pieces. Signed in, as a member of the request's site (the server checks every
 * action); a signed-out visitor goes to sign-in and comes straight back here.
 */

const REQUEST_FIELDS = `
  id
  accountId
  accountName
  note
  maxFiles
  status
  expiresAt
  assets { assetId filename url kind status }
`;
const GET_REQUEST = gql`
  query MediaUploadRequest($token: String!) {
    mediaUploadRequest(token: $token) { ${REQUEST_FIELDS} }
  }
`;
const ADD = gql`
  mutation AddToMediaUploadRequest($token: String!, $assetId: ID!) {
    addToMediaUploadRequest(token: $token, assetId: $assetId) { ${REQUEST_FIELDS} }
  }
`;
const FINISH = gql`
  mutation FinishMediaUploadRequest($token: String!) {
    finishMediaUploadRequest(token: $token) { ${REQUEST_FIELDS} }
  }
`;

interface RequestData {
  id: string;
  accountId: string;
  accountName: string;
  note?: string | null;
  maxFiles: number;
  status: 'open' | 'done' | 'expired';
  expiresAt: string;
  assets: Array<{
    assetId: string;
    filename?: string | null;
    url?: string | null;
    kind?: string | null;
    status?: string | null;
  }>;
}

interface Pending {
  key: string;
  name: string;
  preview: string | null;
  state: 'uploading' | 'failed';
  error?: string;
  percent?: number;
}

const MAX_BYTES = 24 * 1024 * 1024;

export function UploadRequestPage() {
  const { token = '' } = useParams();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { data, loading, error, refetch } = useQuery<{ mediaUploadRequest: RequestData }>(
    GET_REQUEST,
    { variables: { token }, skip: !isAuthenticated, fetchPolicy: 'network-only' }
  );
  const [add] = useMutation(ADD);
  const client = useApolloClient();
  const [finish, { loading: finishing }] = useMutation(FINISH);
  const [pending, setPending] = useState<Pending[]>([]);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  if (isLoading)
    return (
      <Shell>
        <Loader />
      </Shell>
    );
  if (!isAuthenticated) {
    // Back here after signing in (TinycmsLogin reads postLoginRedirect).
    sessionStorage.setItem('postLoginRedirect', location.pathname);
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (loading && !data)
    return (
      <Shell>
        <Loader />
      </Shell>
    );
  if (error || !data) {
    const msg = error?.graphQLErrors?.[0]?.message || 'This upload link didn’t open.';
    return (
      <Shell>
        <h1 className="upr-title">This link didn’t open</h1>
        <p className="upr-lede">{msg}</p>
        <p className="upr-muted">
          Uploads go to a site, so you need to be signed in as someone on it. Ask your agent for a
          new link if this one is old.
        </p>
      </Shell>
    );
  }

  const req = data.mediaUploadRequest;
  const used = req.assets.length + pending.filter((p) => p.state === 'uploading').length;
  const left = Math.max(0, req.maxFiles - used);

  const sendFiles = async (files: File[]) => {
    const take = files
      .filter((f) => f.type.startsWith('image/') || /\.heic$/i.test(f.name) || isVideoFile(f))
      .slice(0, left);
    for (const file of take) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const video = isVideoFile(file);
      const item: Pending = {
        key,
        name: file.name,
        preview: video ? null : URL.createObjectURL(file),
        state: 'uploading',
      };
      setPending((p) => [...p, item]);
      try {
        let assetId: string;
        if (video) {
          ({ assetId } = await uploadVideo(client, req.accountId, file, (percent) =>
            setPending((p) => p.map((x) => (x.key === key ? { ...x, percent } : x)))
          ));
        } else {
          if (file.size > MAX_BYTES) throw new Error('Larger than 24 MB');
          ({ assetId } = await uploadAsset(req.accountId, file, { filename: file.name }));
        }
        await add({ variables: { token, assetId } });
        await refetch();
        setPending((p) => p.filter((x) => x.key !== key));
        if (item.preview) URL.revokeObjectURL(item.preview);
      } catch (err) {
        setPending((p) =>
          p.map((x) =>
            x.key === key
              ? {
                  ...x,
                  state: 'failed',
                  error: err instanceof Error ? err.message : 'Upload failed',
                }
              : x
          )
        );
      }
    }
  };

  if (req.status !== 'open') {
    return (
      <Shell>
        <h1 className="upr-title">{req.status === 'done' ? 'All set' : 'This link has expired'}</h1>
        <p className="upr-lede">
          {req.status === 'done'
            ? `${req.assets.length} ${req.assets.length === 1 ? 'file is' : 'files are'} with your agent. Go back to it and tell it you’re done.`
            : 'Ask your agent for a new upload link.'}
        </p>
        <Grid assets={req.assets} pending={[]} />
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="upr-title">Add photos or videos for your agent</h1>
      <p className="upr-lede">For “{req.accountName}”. They go to the site’s media library.</p>
      {req.note ? <blockquote className="upr-note">{req.note}</blockquote> : null}

      <div
        className="upr-drop"
        data-dragging={dragging ? 'true' : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void sendFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={left === 0}
          onClick={() => input.current?.click()}
        >
          Choose files
        </button>
        <div className="upr-muted">
          {left === 0
            ? `That’s the ${req.maxFiles} this link takes.`
            : `or drop them here · up to ${left} more · photos up to 24 MB, videos up to 4 GB`}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => {
            void sendFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
      </div>

      <Grid assets={req.assets} pending={pending} />

      <div className="upr-actions">
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={!req.assets.length || finishing || pending.some((p) => p.state === 'uploading')}
          onClick={async () => {
            await finish({ variables: { token } });
            await refetch();
          }}
        >
          I’m done
        </button>
        <span className="upr-muted">Then tell your agent. It picks them up from here.</span>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="upr">
      <div className="upr-brand">
        <SwallowMark className="ink-mark-swallow" />
        <TinycmsWordmark />
      </div>
      <main className="upr-card">{children}</main>
    </div>
  );
}

function Grid({ assets, pending }: { assets: RequestData['assets']; pending: Pending[] }) {
  if (!assets.length && !pending.length) return null;
  return (
    <div className="upr-grid">
      {assets.map((a) => (
        <figure key={a.assetId} className="upr-tile">
          {a.url ? (
            <img src={a.url} alt="" />
          ) : (
            <div className="upr-noimg">
              {a.kind === 'video' ? 'Video — getting it ready' : 'No preview'}
            </div>
          )}
          <figcaption>✓ {a.filename || 'Uploaded'}</figcaption>
        </figure>
      ))}
      {pending.map((p) => (
        <figure key={p.key} className="upr-tile" data-state={p.state}>
          {p.preview ? <img src={p.preview} alt="" /> : <div className="upr-noimg">Video</div>}
          <figcaption>
            {p.state === 'uploading'
              ? `Uploading${p.percent != null ? ` ${p.percent}%` : '…'}`
              : `Didn’t upload: ${p.error}`}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
